import { useAtomValue } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import { useFirebaseFileUpload } from '@ekhodealer/ekho-common/storage';

import type { SignedUploadUrlResponse, UploadInput } from '@ekhodealer/ekho-common/storage';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';

import { drawerAtom } from '../atoms/editDrawerAtoms';

export interface BlemishUploadTask {
	/** Unique ID for this upload task */
	id: string;
	/** The file being uploaded */
	file: File;
	/** Upload progress 0–100 */
	progress: number;
	/** Current upload status */
	status: 'uploading' | 'success' | 'error';
	/** Public GCS URL, populated on success */
	publicUrl?: string;
	/** Error details, populated on failure */
	error?: unknown;
}

/**
 * Hook for uploading blemish images via a backend-generated GCS signed URL.
 * Delegates upload mechanics to `useFirebaseFileUpload` from ekho-common.
 * Stores the public GCS URL on success so images can be rendered directly.
 */
export const useBlemishImageUpload = (): {
	tasks: BlemishUploadTask[];
	uploadImages: (files: File[]) => void;
	isUploading: boolean;
	clearTasks: () => void;
} => {
	const servicesProviderId = useCurrentServicesProviderId();
	const { selectedItemId } = useAtomValue(drawerAtom);
	const [clearedIds, setClearedIds] = useState<Set<string>>(new Set());

	const getUploadUrl = useCallback(
		async (file: File): Promise<SignedUploadUrlResponse> => {
			const response = await apiClient.post<SignedUploadUrlResponse>(
				inventoryEndpoints.cosmeticReportUploadUrl(servicesProviderId),
				{
					inventoryItemId: selectedItemId ?? 'unknown',
					fileName: file.name,
					contentType: file.type,
				},
			);
			return response.data;
		},
		[servicesProviderId, selectedItemId],
	);

	const { tasks: rawTasks, addFiles, isUploading } = useFirebaseFileUpload({ getUploadUrl });

	// Keep a stable ref to rawTasks so clearTasks doesn't change identity on every progress event
	const rawTasksRef = useRef(rawTasks);
	rawTasksRef.current = rawTasks;

	const tasks: BlemishUploadTask[] = rawTasks
		.filter((t) => !clearedIds.has(t.id))
		.map((t) => {
			const baseTask = { id: t.id, file: t.file, progress: t.progress };
			if (t.status === 'success') {
				return { ...baseTask, status: 'success' as const, publicUrl: t.publicUrl, error: undefined };
			}
			if (t.status === 'error' || t.status === 'canceled') {
				return { ...baseTask, status: 'error' as const, error: t.error };
			}
			// idle | running → uploading
			return { ...baseTask, status: 'uploading' as const };
		});

	const uploadImages = useCallback(
		(files: File[]) => {
			const inputs: UploadInput[] = files.map((file) => ({ file }));
			addFiles(inputs);
		},
		[addFiles],
	);

	const clearTasks = useCallback(() => {
		setClearedIds((prev) => {
			const next = new Set(prev);
			rawTasksRef.current.forEach((t) => next.add(t.id));
			return next;
		});
	}, []);

	return { tasks, uploadImages, isUploading, clearTasks };
};
